<template>
  <table class="risk-table text-left">
    <tr :class="anyExpanded ? '' : 'collapsed'">
      <th id="tier" @click="toggleSort('tier')">
        Tier
        <AppkitIcon
          v-if="sortColumn == 'tier'"
          :icon="`icon-arrow-${ascending ? 'up' : 'down'}-small-outline`"
        />
      </th>
      <th id="companyName" @click="toggleSort('companyName')">
        Company name
        <AppkitIcon
          v-if="sortColumn == 'companyName'"
          :icon="`icon-arrow-${ascending ? 'up' : 'down'}-small-outline`"
        />
      </th>
      <th id="country" @click="toggleSort('countryOfOperation')">
        Country
        <AppkitIcon
          v-if="sortColumn == 'countryOfOperation'"
          :icon="`icon-arrow-${ascending ? 'up' : 'down'}-small-outline`"
        />
      </th>
      <th id="flagCount" @click="toggleSort('flags')">
        No. flags
        <AppkitIcon
          v-if="sortColumn == 'flags'"
          :icon="`icon-arrow-${ascending ? 'up' : 'down'}-small-outline`"
        />
      </th>
      <th @click="toggleSort('industry')">
        Industry
        <AppkitIcon
          v-if="sortColumn == 'industry'"
          :icon="`icon-arrow-${ascending ? 'up' : 'down'}-small-outline`"
        />
      </th>
      <th v-if="showRiskTypeColumn">Risk type</th>
      <th id="detailedReason">
        Detailed reason
        <span @click="toggleExpandAll" class="expand-collapse-button">
          {{ anyExpanded ? "Collapse all" : "Expand all" }}
        </span>
      </th>
    </tr>
    <tbody v-for="companyRisk in sortedData">
      <template
        v-for="(indicatorTypeReason, i) in companyRisk.reasons"
        :key="indicatorTypeReason.indicatorType"
      >
        <tr
          v-for="(detailedReason, j) in indicatorTypeReason.detailedReasons"
          :key="detailedReason.reason"
          :class="
            collapsedCompanies[companyRisk.companyName] ? 'collapsed' : ''
          "
          @click="toggleCollapsed(companyRisk.companyName)"
        >
          <template v-if="i == 0 && j == 0">
            <td :rowspan="getRiskStatLength(companyRisk)" class="text-center">
              {{ companyRisk.tierLevel }}
            </td>
            <td :rowspan="getRiskStatLength(companyRisk)">
              <p
                class="companyName"
                @click.stop.prevent="goToUnitProfile(companyRisk)"
              >
                {{ companyRisk.companyName }}
              </p>
            </td>
            <td :rowspan="getRiskStatLength(companyRisk)">
              <CountryText :country-code="companyRisk.countryOfOperation.substring(0, 2)" />
            </td>
            <td :rowspan="getRiskStatLength(companyRisk)" class="text-center">
              {{ getRiskStatLength(companyRisk) }}
            </td>
            <td :rowspan="getRiskStatLength(companyRisk)">
              {{ companyRisk.industry }}
            </td>
          </template>
          <td
            v-if="showRiskTypeColumn && j == 0"
            :rowspan="indicatorTypeReason.detailedReasons.length"
          >
            {{ indicatorTypeToReasonText(indicatorTypeReason.indicatorType) }}
            ({{ indicatorTypeReason.detailedReasons.length }})
          </td>
          <td id="detailedReason">
            <p>{{ detailedReason.reason }}</p>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import type { RiskStatsDto } from "@/models/dto/riskStatsDto";
import { indicatorTypeToReasonText } from "@/models/enum/indicatorType";
import { ref, type Ref, computed, watch } from "vue";
import CountryText from "@/components/custom/CountryText.vue";
import AppkitIcon from "@/components/custom/AppkitIcon.vue";
import { countryIndex } from "@/modules/countries";

const props = defineProps<{
  data: RiskStatsDto[];
  showRiskTypeColumn: boolean;
}>();

watch(
  () => props.data,
  (x) => {
    sortedData.value = props.data;
    sortColumn.value = "";
    ascending.value = true;
  },
  { deep: true }
);
const sortedData = ref(props.data);

const emit = defineEmits<{
  (e: "goToUnitProfile", risk: RiskStatsDto): void;
}>();

const collapsedCompanies: Ref<{ [companyName: string]: boolean }> = ref(
  props.data.reduce((acc, curr) => ({ ...acc, [curr.companyName]: true }), {})
);

const anyExpanded = computed(() =>
  Object.values(collapsedCompanies.value).some((item) => !item)
);

const ascending = ref(true);
const sortColumn = ref("");

function toggleSort(column: string) {
  if (column != sortColumn.value) {
    sortColumn.value = column;
    ascending.value = true;
  } else {
    ascending.value = !ascending.value;
  }

  sortBy(column);
}

function sortBy(column: string) {
  const sortValue = ascending.value ? -1 : 1;

  const propertyName = column as keyof RiskStatsDto;

  if (propertyName == "countryOfOperation") {
    sortedData.value = [...props.data].sort(
      (a: RiskStatsDto, b: RiskStatsDto) => {
        if (countryIndex[a[propertyName]] > countryIndex[b[propertyName]]) {
          return sortValue;
        } else if (
          countryIndex[a[propertyName]] < countryIndex[b[propertyName]]
        ) {
          return -sortValue;
        } else {
          return 0;
        }
      }
    );
  } else if (column == "flags") {
    sortedData.value = [...props.data].sort(
      (a: RiskStatsDto, b: RiskStatsDto) => {
        if (getRiskStatLength(a) > getRiskStatLength(b)) {
          return sortValue;
        } else if (getRiskStatLength(a) < getRiskStatLength(b)) {
          return -sortValue;
        } else {
          return 0;
        }
      }
    );
  }
  else {
    sortedData.value = [...props.data].sort(
      (a: RiskStatsDto, b: RiskStatsDto) => {
        if (a[propertyName] > b[propertyName]) {
          return sortValue;
        } else if (a[propertyName] < b[propertyName]) {
          return -sortValue;
        } else {
          return 0;
        }
      }
    );
  }
}

function toggleExpandAll() {
  const collapse = anyExpanded.value;
  Object.keys(collapsedCompanies.value).forEach((companyName) => {
    collapsedCompanies.value[companyName] = collapse;
  });
}

function toggleCollapsed(companyName: string) {
  collapsedCompanies.value[companyName] =
    !collapsedCompanies.value[companyName];
}

function getRiskStatLength(companyRisk: RiskStatsDto) {
  var counter = 0;

  companyRisk.reasons.forEach((r) => {
    counter += r.detailedReasons.length;
  });

  return counter;
}

function goToUnitProfile(risk: RiskStatsDto) {
  emit("goToUnitProfile", risk);
}
</script>

<style scoped lang="scss">
.risk-table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;

  tbody:hover {
    background-color: var(--light-grey-4);
    cursor: pointer;
  }

  th:hover {
    cursor: pointer;
    border-bottom: 2px solid var(--grey);
  }

  th {
    font-weight: 500;
  }

  .companyName:hover {
    font-weight: 700;
  }

  th,
  td {
    vertical-align: text-top;
    padding: 6px;
    border-bottom: 1px solid var(--grey20);
  }

  #tier {
    width: 70px;
  }

  #flagCount {
    width: 110px;
  }

  #country {
    width: 120px;
  }

  #detailedReason {
    transition: width 500ms;
    width: 40%;
  }

  .collapsed {
    #detailedReason {
      transition: width 500ms;
      width: 25%;

      p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

.expand-collapse-button {
  float: right;
  color: var(--tangerine);
  padding-bottom: 1px;
}
</style>
